import { observer } from 'mobx-react-lite';
import type { TurnWordChoice } from '@/game/core/turn';
import { DirectionArrow, type ArrowDirection } from './direction-arrow';
import { GameImage } from './game-image';
import type { ShiritoriGameStore } from '@/game/core/game.store';
import toast from 'react-hot-toast';
import type { ShiritoriSquareState } from '@/game/core/game';
import { useTranslation } from 'react-i18next';

export type PlayFeedback = 'primary' | 'error';

interface ShiritoriSquareProps {
    game: ShiritoriGameStore;
    word?: TurnWordChoice;
    state?: ShiritoriSquareState;
    arrow: ArrowDirection;
    feedback?: PlayFeedback;
    onFeedbackComplete?: () => void;
}

export const ShiritoriSquare = observer(function ({ game, word, state, arrow, feedback, onFeedbackComplete }: ShiritoriSquareProps) {
    const { t } = useTranslation();
    const isLargeBoard = game.config?.boardSize === 5 || game.config?.boardSize === 7;

    function tapImage() {
        if (word && game.areHintsAllowed) {
            toast(word.kana, {
                className: 'bg-background rounded shadow-lg',
                position: 'top-center',
                duration: 2000,
            });
        }
    }

    let stateClasses = '';
    if (state === 'current') {
        stateClasses = 'ring-2 ring-game-highlight/50 shadow-lg';
    } else if (state === 'filled') {
        stateClasses = '';
    } else if (state === 'empty') {
        stateClasses = 'border-dashed opacity-50';
    }

    const shouldAnimateFeedback = Boolean(feedback);
    const feedbackClasses = shouldAnimateFeedback
        ? `animate-square-play ${feedback === 'error' ? 'animate-square-play-error' : 'animate-square-play-primary'}`
        : '';

    return (
        <div
            className={`relative aspect-square border-2 rounded-lg flex items-center justify-center shadow-md border-game-highlight/30 bg-background ${stateClasses} ${feedbackClasses}`}
            onAnimationEnd={(event) => {
                if (event.currentTarget === event.target && shouldAnimateFeedback) {
                    onFeedbackComplete?.();
                }
            }}
        >
            {arrow && (
                <DirectionArrow direction={arrow} />
            )}

            {state === 'timeout' && (
                <div className="flex flex-col items-center justify-center text-center text-game-highlight px-1">
                    <div className="text-xs font-semibold leading-tight">{t('game.gameplay.board.timeoutTitle')}</div>
                    <div className="text-sm uppercase tracking-wide">{t('game.gameplay.board.timeoutSubtitle')}</div>
                </div>
            )}

            {state !== 'empty' && word && (
                <div onClick={tapImage} className="flex flex-col items-center justify-center text-center px-1">
                    <GameImage game={game} match={word} className={isLargeBoard ? 'p-1' : 'p-2'} />
                </div>
            )}
        </div>
    );
});
